<template>
  <v-container fluid :class="modoCorrigirPrimeiro ? 'pt-0 px-0' : 'px-7 py-6'">
    <v-row>
      <v-col cols="12" class="pt-0">
        <div v-if="!modoCorrigirPrimeiro" class="font-weight-bold grey--text text--darken-1 text-overline">Executar tarefa</div>
        <div class="align-center d-flex mt-1">
          <h2 class="font-weight-medium ma-0 text-h6">Justificar erro</h2>
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-btn aria-label="Como justificar o erro" class="ml-1 mt-2" color="primary" icon small v-bind="attrs" v-on="on">
                <v-icon>mdi-help-circle-outline</v-icon>
              </v-btn>
            </template>
            <span class="d-inline-block" style="max-width: 340px">
              Escolha uma evidência para justificar o erro. A finalização só é liberada quando a evidência selecionada estiver
              válida.
            </span>
          </v-tooltip>
        </div>
      </v-col>
    </v-row>

    <v-form class="mt-2" @submit.prevent="finalizar">
      <v-row>
        <v-col cols="12" class="pb-0">
          <v-select
            v-model="tipoJustificativa"
            :items="tiposJustificativa"
            :rules="[regraObrigatoria]"
            item-text="text"
            item-value="value"
            label="Forma de justificativa"
            outlined
            @change="limparJustificativa"
          />
        </v-col>
      </v-row>

      <v-row v-if="tipoJustificativa === 'FOTO'">
        <v-col cols="12" class="pa-0">
          <upload-files
            ref="uploadFotos"
            :allowedFiles="fotosPermitidas"
            :validarArquivo="validarFoto"
            :maxFiles="5"
            uploadFieldName="imagem"
            uploadMethod="uploadImagem"
            uploadPath="gestao-bdgds-erros/evidencias"
            class="mb-0"
            @file:uploaded="adicionarFotosEnviadas"
          />
        </v-col>

        <v-col v-for="foto in fotosEnviadas" :key="foto.id" cols="12" md="4">
          <v-card outlined tile>
            <v-card-title class="body-2 py-2">{{ foto.arquivo }}</v-card-title>
            <div style="position: relative">
              <v-img v-if="foto.preview" :src="foto.preview" aspect-ratio="1" />
              <v-responsive v-else aspect-ratio="1" class="align-center d-flex justify-center">
                <v-skeleton-loader v-if="foto.carregandoPreview" height="100%" tile type="image" width="100%" />
                <span v-else class="caption grey--text">Não foi possível carregar a imagem.</span>
              </v-responsive>
              <v-btn
                absolute
                color="error"
                dark
                fab
                right
                top
                x-small
                :aria-label="`Excluir ${foto.arquivo}`"
                :loading="fotosExcluindo.includes(foto.id)"
                style="z-index: 2"
                @click="excluirFoto(foto)"
              >
                <v-icon x-small>mdi-close</v-icon>
              </v-btn>
            </div>
          </v-card>
        </v-col>
      </v-row>

      <v-row v-else-if="tipoJustificativa === 'RELATORIO'">
        <v-col cols="12" class="py-0">
          <v-text-field
            :value="relatoriosSelecionados.length ? `${relatoriosSelecionados.length} relatório(s) selecionado(s)` : ''"
            append-icon="mdi-magnify"
            label="Relatórios"
            outlined
            readonly
            @click="abrirSeletorRelatorio"
            @click:append="abrirSeletorRelatorio"
          />
        </v-col>

        <v-col v-if="relatoriosSelecionados.length" cols="12" class="pb-2 pt-0">
          <evidencias-selecionadas
            :evidencias="relatoriosSelecionados.map((relatorio) => ({ id: relatorio.id, titulo: relatorio.nome }))"
            icone="mdi-file-document-outline"
            @remover="removerRelatorio"
          />
        </v-col>
      </v-row>

      <v-row v-else-if="tipoJustificativa === 'EXTRACAO_BDGD'">
        <v-col cols="12" class="py-0">
          <v-text-field
            :value="extracoesSelecionadas.length ? `${extracoesSelecionadas.length} extração(ões) selecionada(s)` : ''"
            append-icon="mdi-magnify"
            label="Extrações BDGD"
            outlined
            readonly
            @click="abrirSeletorExtracao"
            @click:append="abrirSeletorExtracao"
          />
        </v-col>

        <v-col v-if="extracoesSelecionadas.length" cols="12" class="py-0">
          <evidencias-selecionadas
            :evidencias="
              extracoesSelecionadas.map((extracao) => ({
                id: extracao.id,
                titulo: extracao.titulo,
                subtitulo: extracao.tabela || '—'
              }))
            "
            classe-cabecalho="pb-7"
            icone="mdi-database-export-outline"
            @remover="removerExtracao"
          />
        </v-col>
      </v-row>

      <v-row v-else-if="tipoJustificativa === 'CONSULTA_SQL'">
        <v-col cols="12" class="py-0">
          <v-menu v-model="menuDataConsulta" :close-on-content-click="false" min-width="auto" offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="dataConsultaFormatada"
                :rules="[regraObrigatoria]"
                append-icon="mdi-calendar"
                label="Data da consulta"
                outlined
                readonly
                v-bind="attrs"
                v-on="on"
              />
            </template>
            <v-date-picker v-model="dataConsulta" locale="pt-br" @input="menuDataConsulta = false" />
          </v-menu>
        </v-col>

        <v-col cols="12" class="py-0">
          <v-radio-group v-model="modoConsultaSql" class="mt-0" row>
            <v-radio label="Escrever texto" value="TEXTO" />
            <v-radio label="Importar arquivo(s) SQL" value="ARQUIVOS" />
          </v-radio-group>
        </v-col>

        <v-col v-if="modoConsultaSql === 'TEXTO'" cols="12" class="py-0">
          <v-textarea v-model="consultaSql" :rules="[regraObrigatoria]" auto-grow label="Consulta SQL" outlined rows="7" />
        </v-col>

        <v-col v-else cols="12" class="py-0">
          <upload-files
            ref="uploadSql"
            :allowedFiles="arquivosSqlPermitidos"
            :validarArquivo="validarArquivoSql"
            uploadMethod="uploadSql"
            class="mb-0"
            @file:uploaded="adicionarArquivosSqlEnviados"
          />

          <evidencias-selecionadas
            class="mb-4"
            :evidencias="arquivosSqlEnviados.map((arquivo) => ({ id: arquivo.id, titulo: arquivo.arquivo }))"
            icone="mdi-file-code-outline"
            @remover="removerArquivoSql"
          />
        </v-col>
      </v-row>

      <v-row v-else-if="tipoJustificativa === 'VALIDACAO_BDGD'">
        <v-col cols="12" class="py-0">
          <v-text-field
            :value="validacoesSelecionadas.length ? `${validacoesSelecionadas.length} validação(ões) selecionada(s)` : ''"
            append-icon="mdi-magnify"
            label="Validações BDGD"
            outlined
            readonly
            @click="abrirSeletorValidacao"
            @click:append="abrirSeletorValidacao"
          />
        </v-col>

        <v-col v-if="validacoesSelecionadas.length" cols="12" class="py-0">
          <evidencias-selecionadas
            :evidencias="
              validacoesSelecionadas.map((validacao) => ({
                id: validacao.id,
                titulo: `Validação BDGD #${validacao.id}`,
                subtitulo: validacao.status || '—'
              }))
            "
            icone="mdi-check"
            @remover="removerValidacao"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" class="py-0">
          <v-checkbox
            v-if="modoCorrigirPrimeiro"
            class="mb-3 mt-0"
            :input-value="origemCorrigida"
            label="Corrigir origem"
            @change="$emit('update:origem-corrigida', $event)"
          />
          <v-select
            v-if="modoCorrigirPrimeiro && origemCorrigida"
            :value="origemErro"
            :items="origensErro"
            :rules="[regraObrigatoria]"
            item-text="text"
            item-value="value"
            label="Origem do erro"
            outlined
            @input="$emit('update:origem-erro', $event)"
          />
          <observacao-tarefa v-model="observacao" :rows="5" />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" class="pt-0">
          <v-alert v-if="erroJustificativa" class="mb-4" dense outlined type="error">{{ erroJustificativa }}</v-alert>

          <div class="d-flex justify-end mt-8">
            <botao-confirmar-tarefa label="Justificar" :disabled="!formularioPreenchido" :loading="executando" />
          </div>
        </v-col>
      </v-row>
    </v-form>

    <dialog-seletor-relatorio
      v-if="dialogSeletorRelatorio"
      :activated.sync="dialogSeletorRelatorio"
      :selecionados="relatoriosSelecionados"
      @selected="selecionarRelatorio"
    />
    <dialog-seletor-extracao-bdgd
      v-if="dialogSeletorExtracao"
      :activated.sync="dialogSeletorExtracao"
      :bdgd-id="bdgd.id"
      :entidade="erro.entidade"
      :selecionados="extracoesSelecionadas"
      @selected="selecionarExtracao"
    />
    <dialog-seletor-validacao-bdgd
      v-if="dialogSeletorValidacao"
      :activated.sync="dialogSeletorValidacao"
      :erro-id="erro.id"
      :selecionados="validacoesSelecionadas"
      @selected="selecionarValidacao"
    />
  </v-container>
</template>

<script>
  import FilesService from '@/services/FilesService.js';
  import GestaoBdgdsErrosTarefasService from '@/services/GestaoBdgdsErrosTarefasService.js';
  import { origensErro } from './origensErro.js';
  import UploadFiles from '@/components/general/UploadFiles/Index.vue';
  import BotaoConfirmarTarefa from './BotaoConfirmarTarefa.vue';
  import DialogSeletorExtracaoBdgd from './DialogSeletorExtracaoBdgd.vue';
  import DialogSeletorRelatorio from './DialogSeletorRelatorio.vue';
  import DialogSeletorValidacaoBdgd from './DialogSeletorValidacaoBdgd.vue';
  import EvidenciasSelecionadas from './EvidenciasSelecionadas.vue';
  import ObservacaoTarefa from './ObservacaoTarefa.vue';

  const tiposJustificativa = [
    { text: 'Validação de BDGD', value: 'VALIDACAO_BDGD' },
    { text: 'Consulta SQL', value: 'CONSULTA_SQL' },
    { text: 'Imagem(s)', value: 'FOTO' },
    { text: 'Relatório', value: 'RELATORIO' },
    { text: 'Extração BDGD', value: 'EXTRACAO_BDGD' }
  ];

  export default {
    name: 'JustificarErro',

    components: {
      BotaoConfirmarTarefa,
      DialogSeletorExtracaoBdgd,
      DialogSeletorRelatorio,
      DialogSeletorValidacaoBdgd,
      EvidenciasSelecionadas,
      ObservacaoTarefa,
      UploadFiles
    },

    props: {
      bdgd: {
        type: Object,
        default: () => ({})
      },
      erro: {
        type: Object,
        required: true
      },
      tarefa: {
        type: Object,
        required: true
      },
      modoCorrigirPrimeiro: {
        type: Boolean,
        default: false
      },
      origemCorrigida: {
        type: Boolean,
        default: false
      },
      origemErro: {
        type: String,
        default: null
      }
    },

    data() {
      return {
        tiposJustificativa,
        origensErro,
        tipoJustificativa: 'CONSULTA_SQL',
        fotosPermitidas: ['jpg', 'jpeg', 'png'],
        arquivosSqlPermitidos: ['sql'],
        fotosEnviadas: [],
        arquivosSqlEnviados: [],
        fotosExcluindo: [],
        dialogSeletorRelatorio: false,
        relatoriosSelecionados: [],
        dialogSeletorExtracao: false,
        extracoesSelecionadas: [],
        dialogSeletorValidacao: false,
        validacoesSelecionadas: [],
        modoConsultaSql: 'TEXTO',
        consultaSql: '',
        dataConsulta: null,
        menuDataConsulta: false,
        observacao: '',
        executando: false,
        erroJustificativa: ''
      };
    },

    computed: {
      formularioPreenchido() {
        const justificativaPreenchida =
          (this.tipoJustificativa === 'FOTO' && this.fotosEnviadas.length > 0) ||
          (this.tipoJustificativa === 'RELATORIO' && this.relatoriosSelecionados.length > 0) ||
          (this.tipoJustificativa === 'EXTRACAO_BDGD' && this.extracoesSelecionadas.length > 0) ||
          (this.tipoJustificativa === 'VALIDACAO_BDGD' && this.validacoesSelecionadas.length > 0) ||
          (this.tipoJustificativa === 'CONSULTA_SQL' &&
            Boolean(this.dataConsulta) &&
            (this.modoConsultaSql === 'TEXTO' ? Boolean(this.consultaSql.trim()) : this.arquivosSqlEnviados.length > 0));

        return justificativaPreenchida && (!this.modoCorrigirPrimeiro || !this.origemCorrigida || Boolean(this.origemErro));
      },

      dataConsultaFormatada() {
        if (!this.dataConsulta) return null;

        const [ano, mes, dia] = this.dataConsulta.split('-');
        return `${dia}/${mes}/${ano}`;
      }
    },

    beforeUnmount() {
      this.limparPreviewsFotos();
    },

    methods: {
      regraObrigatoria(valor) {
        return !!valor || 'Campo obrigatório.';
      },

      limparJustificativa() {
        this.erroJustificativa = '';
        this.limparPreviewsFotos();
        this.fotosEnviadas = [];
        this.dialogSeletorRelatorio = false;
        this.relatoriosSelecionados = [];
        this.dialogSeletorExtracao = false;
        this.extracoesSelecionadas = [];
        this.dialogSeletorValidacao = false;
        this.validacoesSelecionadas = [];
        this.modoConsultaSql = 'TEXTO';
        this.arquivosSqlEnviados = [];
        this.consultaSql = '';
        this.dataConsulta = null;
        this.menuDataConsulta = false;
      },

      validarFoto(item) {
        const tiposPermitidos = ['image/jpeg', 'image/png'];

        if (!tiposPermitidos.includes(item.fileObj.type)) return 'Envie uma foto nos formatos JPG, JPEG ou PNG.';
        if (item.fileObj.size > 10 * 1024 * 1024) return 'A foto deve ter no máximo 10 MB.';

        return null;
      },

      validarArquivoSql(item) {
        if (
          !String(item.name || '')
            .toLowerCase()
            .endsWith('.sql')
        ) {
          return 'Envie apenas arquivos no formato SQL (.sql).';
        }

        if (item.fileObj.size > 5 * 1024 * 1024) return 'Cada arquivo SQL deve ter no máximo 5 MB.';

        return null;
      },

      adicionarArquivosSqlEnviados({ files }) {
        this.erroJustificativa = '';

        files.forEach((arquivo) => {
          if (!this.arquivosSqlEnviados.some((item) => item.id === arquivo.id)) {
            this.arquivosSqlEnviados.push(arquivo);
          }
        });
      },

      removerArquivoSql(arquivoId) {
        this.arquivosSqlEnviados = this.arquivosSqlEnviados.filter((arquivo) => arquivo.id !== arquivoId);
        this.$refs.uploadSql?.removerArquivoEnviado(arquivoId);
      },

      async adicionarFotosEnviadas({ files }) {
        this.erroJustificativa = '';

        for (const foto of files) {
          if (this.fotosEnviadas.some((item) => item.id === foto.id)) continue;

          const fotoEnviada = { ...foto, carregandoPreview: true, preview: null };
          this.fotosEnviadas.push(fotoEnviada);

          try {
            const { data } = await FilesService.download(foto.id, null, this.caminhoImagem(foto));
            const preview = URL.createObjectURL(new Blob([data]));
            const indice = this.fotosEnviadas.findIndex((item) => item.id === foto.id);

            if (indice === -1) {
              URL.revokeObjectURL(preview);
              continue;
            }

            this.$set(this.fotosEnviadas[indice], 'preview', preview);
            this.$set(this.fotosEnviadas[indice], 'carregandoPreview', false);
          } catch (error) {
            this.erroJustificativa = 'A foto foi enviada, mas não foi possível carregar a prévia.';
            const indice = this.fotosEnviadas.findIndex((item) => item.id === foto.id);

            if (indice !== -1) this.$set(this.fotosEnviadas[indice], 'carregandoPreview', false);
          }
        }
      },

      limparPreviewsFotos() {
        this.fotosEnviadas.forEach((foto) => {
          if (foto.preview) URL.revokeObjectURL(foto.preview);
        });
      },

      caminhoImagem(foto) {
        return `imagens/gestao-bdgds-erros/evidencias/imagem-${foto.id}.${foto.extensao}`;
      },

      async excluirFoto(foto) {
        if (this.fotosExcluindo.includes(foto.id)) return;

        this.fotosExcluindo.push(foto.id);
        this.erroJustificativa = '';

        try {
          await FilesService.delete(foto.id, this.caminhoImagem(foto));
          if (foto.preview) URL.revokeObjectURL(foto.preview);
          this.fotosEnviadas = this.fotosEnviadas.filter((item) => item.id !== foto.id);
          this.$refs.uploadFotos.removerArquivoEnviado(foto.id);
        } catch (error) {
          this.erroJustificativa = 'Não foi possível excluir a imagem enviada.';
        } finally {
          this.fotosExcluindo = this.fotosExcluindo.filter((id) => id !== foto.id);
        }
      },

      abrirSeletorRelatorio() {
        this.erroJustificativa = '';
        this.dialogSeletorRelatorio = true;
      },

      selecionarRelatorio(relatorios) {
        this.dialogSeletorRelatorio = false;
        this.relatoriosSelecionados = (Array.isArray(relatorios) ? relatorios : [relatorios]).filter(Boolean);
      },

      removerRelatorio(relatorioId) {
        this.relatoriosSelecionados = this.relatoriosSelecionados.filter((relatorio) => relatorio.id !== relatorioId);
      },

      abrirSeletorExtracao() {
        this.erroJustificativa = '';

        if (!this.bdgd.id) {
          this.erroJustificativa = 'Não foi possível identificar a versão da BDGD selecionada.';
          return;
        }

        this.dialogSeletorExtracao = true;
      },

      selecionarExtracao(extracoes) {
        this.dialogSeletorExtracao = false;
        this.extracoesSelecionadas = (Array.isArray(extracoes) ? extracoes : [extracoes]).filter(Boolean);
      },

      removerExtracao(extracaoId) {
        this.extracoesSelecionadas = this.extracoesSelecionadas.filter((extracao) => extracao.id !== extracaoId);
      },

      abrirSeletorValidacao() {
        this.erroJustificativa = '';
        this.dialogSeletorValidacao = true;
      },

      selecionarValidacao(validacoes) {
        this.dialogSeletorValidacao = false;
        this.validacoesSelecionadas = (Array.isArray(validacoes) ? validacoes : [validacoes]).filter(Boolean);
      },

      removerValidacao(validacaoId) {
        this.validacoesSelecionadas = this.validacoesSelecionadas.filter((validacao) => validacao.id !== validacaoId);
      },

      dadosJustificativa() {
        const dados = {
          tipo_justificativa: this.tipoJustificativa,
          observacao: this.observacao.trim() || null
        };

        if (this.modoCorrigirPrimeiro) {
          dados.acao = 'JUSTIFICAR';
          dados.origem_corrigida = this.origemCorrigida;
          if (this.origemCorrigida) dados.origem_erro = this.origemErro;
        } else {
          dados.tarefa_id = this.tarefa.id;
        }

        if (this.tipoJustificativa === 'FOTO') {
          dados.arquivo_ids = this.fotosEnviadas.map((foto) => foto.id);
        } else if (this.tipoJustificativa === 'RELATORIO') {
          dados.relatorio_ids = this.relatoriosSelecionados.map((relatorio) => relatorio.id);
        } else if (this.tipoJustificativa === 'EXTRACAO_BDGD') {
          dados.extracao_bdgd_ids = this.extracoesSelecionadas.map((extracao) => extracao.id);
        } else if (this.tipoJustificativa === 'VALIDACAO_BDGD') {
          dados.validacao_bdgd_ids = this.validacoesSelecionadas.map((validacao) => validacao.id);
        } else if (this.tipoJustificativa === 'CONSULTA_SQL') {
          dados.modo_consulta_sql = this.modoConsultaSql;
          if (this.modoConsultaSql === 'TEXTO') {
            dados.consulta_sql = this.consultaSql.trim();
          } else {
            dados.arquivo_ids = this.arquivosSqlEnviados.map((arquivo) => arquivo.id);
          }
          dados.data_consulta = this.dataConsulta;
        }

        return dados;
      },

      async finalizar() {
        if (!this.formularioPreenchido) return;

        this.executando = true;
        this.erroJustificativa = '';

        try {
          const executar = this.modoCorrigirPrimeiro
            ? GestaoBdgdsErrosTarefasService.executarCorrigirPrimeiro
            : GestaoBdgdsErrosTarefasService.justificarTarefa;

          await executar(this.erro.id, this.dadosJustificativa());

          this.$emit('executada');
        } catch (error) {
          this.erroJustificativa = error?.response?.data?.message || 'Não foi possível concluir a justificativa.';
        } finally {
          this.executando = false;
        }
      }
    }
  };
</script>

<style scoped>
  ::v-deep .v-expansion-panel-content__wrap {
    padding: 0 !important;
  }
</style>
